import { createContext, useCallback, useState } from 'react';

import useLocalStorage from '../../hooks/LocalStorage';
import { useAuth } from '../Auth';
import { DOMAIN } from '../Config';

export type ChatOptions = {
  goal?: string;
  step?: string;
  clientKnowledge?: Record<string, any>;
};

export interface BrainSessionContextType {
  chatOptions: ChatOptions | undefined;
  showChatContainer: boolean;
  closeModalUrl: string | undefined;
  userId: string | undefined;
  toggleChat: (options: ChatOptions) => void;
  closeChatModal: () => void;
  saveCloseModalUrl: (url: string | null) => void;
}

const BrainSessionContext = createContext<BrainSessionContextType>(undefined!);

interface BrainSessionProviderProps {
  children?: React.ReactNode;
}

function BrainSessionProvider({ children = null }: BrainSessionProviderProps) {
  const [chatOptions, setChatOptions] = useState<ChatOptions | undefined>(
    () => {
      const params = new URLSearchParams(window.location.search);
      const step = params.get('step');
      const goal = params.get('flow');
      return goal && step ? { step, goal } : undefined;
    },
  );
  const [showChatContainer, setShowChatContainer] = useState(
    Boolean(chatOptions),
  );
  const [closeModalUrl, setCloseModalUrl] = useLocalStorage(
    DOMAIN.closeModalUrl,
    undefined,
  );
  const { userId } = useAuth();

  const saveCloseModalUrl = useCallback(
    (url: string | null) => {
      setCloseModalUrl(url);
    },
    [setCloseModalUrl],
  );

  // TODO: rename to `showChat`
  const toggleChat = useCallback((options: ChatOptions) => {
    setChatOptions(options);
    setShowChatContainer(Boolean(options));
  }, []);

  const closeChatModal = () => {
    setShowChatContainer(false);
    setChatOptions(undefined);
  };

  return (
    <BrainSessionContext.Provider
      value={{
        chatOptions,
        showChatContainer,
        closeModalUrl,
        userId,
        toggleChat,
        closeChatModal,
        saveCloseModalUrl,
      }}
    >
      {children}
    </BrainSessionContext.Provider>
  );
}

export { BrainSessionContext, BrainSessionProvider };
